import { createFileRoute } from '@tanstack/react-router'


import {
    GridColDef
} from '@mui/x-data-grid-premium'

import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import { t } from 'i18next'

import { defaultMemberPermission, memberPermissionSchema } from '@/api/mem/memberPermission'
import { MemberPermissionType } from '@/api/memberPermissions'
import { organizationPermissionsQueryOptions, useCreateOrganizationPermissionMutation, useDeleteOrganizationPermissionMutation, useUpdateOrganizationPermissionMutation } from '@/api/org/organizationPermissionQueryOptions'
import CreateDetailAccordion, { useCreateDetail } from '@/components/detail/CreateDetailRecord'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useOrganizationDetailView from '@/components/hooks/useOrganizationDetailView'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import ScoutIdAutocompleteField from '@/components/ScoutIdAutocompleteField'
import SelectRole from '@/components/select/SelectRole'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/permissions',
)({
    component: OrganizationPermissionsComponent,
})

function OrganizationPermissionsComponent() {
    const params = Route.useParams()

    const { query, data, createable, updateMutation, deleteMutation, onSubmit } = useOrganizationDetailView<MemberPermissionType>(params, organizationPermissionsQueryOptions,
        'MemberPermission', useCreateOrganizationPermissionMutation, useUpdateOrganizationPermissionMutation, useDeleteOrganizationPermissionMutation)

    //const sessionOrganization = useSelector(getOrganization)

    const { xsmall, small, medium, large, xlarge } = useResponsive()
    const basicGridColumns = useBasicGridColumns()

    const columns: GridColDef[] = []

    columns.push({
        field: 'member',
        headerName: t('entity.member'),
        minWidth: 100,
        valueGetter: (_value, row) => `${row.member.firstname || ''} ${row.member.lastname || ''}`,

    })

    columns.push({
        field: 'permission',
        headerName: t('entity.permission'),
        minWidth: 100,
        valueGetter: (_value, row) => `${row.role.name || ''}`,

    })

    if (xsmall || small) {
        columns.push(basicGridColumns.validPeriodColumn)
    }
    if (medium || large || xlarge) {
        columns.push(
            basicGridColumns.descriptionColumn,
        )
        columns.push(
            basicGridColumns.startDateColumn,
            basicGridColumns.endDateColumn,
        )
        columns.push(basicGridColumns.activeColumn)
    }

    const createDetail = useCreateDetail<MemberPermissionType>({
        onSubmit,
        resolver: yupResolver<MemberPermissionType, any, any>(memberPermissionSchema),
        defaultValues: {
            ...defaultMemberPermission,
            organization: {
                uuid: params.organizationUuid,
            },
        },
        title: t('action.new', { entity: t('entity.permission_other') }),
    })

    const editGrid = useEditGrid<MemberPermissionType>({
        defaultValues: defaultMemberPermission,
        updateMutation: updateMutation,
        deleteMutation: deleteMutation,
        records: data,
        columns: columns,
        title: t('entity.permission'),
    })

    if (query.isLoading || query.isFetching) {
        return <LoadProgress />
    }

    return (
        <>
            <Typography>{t('entity.permission_other')}</Typography>
            {createable && (
                <CreateDetailAccordion {...createDetail}>
                    <Grid container spacing={2}>
                        <input
                            type="hidden"
                            defaultValue={params.organizationUuid}
                            {...createDetail.form.register('organization.uuid')}
                        />

                        <Grid
                            size={{
                                xs: 6,
                                md: 4,
                                lg: 3
                            }}>
                            <ScoutIdAutocompleteField
                                name="member"
                                defaultValue={defaultMemberPermission.member.uuid}
                                control={createDetail.form.control}
                                errors={createDetail.form.formState.errors}
                            ></ScoutIdAutocompleteField>
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 4,
                                lg: 3
                            }}>
                            <SelectRole control={createDetail.form.control} name="role" />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregDatePicker
                                name="startDate"
                                defaultValue={dayjs(defaultMemberPermission.startDate)}
                                disabled={false}
                                control={createDetail.form.control}
                                label={t('property.general.startDate')}
                            />
                        </Grid>
                        <></>
                    </Grid>

                </CreateDetailAccordion>
            )}
            <EditGrid {...editGrid}></EditGrid>
        </>
    )
}
